import { VenueRentalConfigurationType } from '@api';
import {
  ActionsCell,
  formatCurrency,
  formatDate,
  formatNumber,
  formatPercent,
} from '@common';
import { DataTableColumn } from 'mantine-datatable';
import { useTranslation } from 'react-i18next';

const TRANS_PATH = 'RENTAL_CONFIGURATION_TABLE.COLUMNS';

type UseRentalConfigurationTableColumnsPropsType = {
  handleDelete: (id: string) => void;
  handleEdit: (rentalConfiguration: VenueRentalConfigurationType) => void;
  userCanEditRentalConfiguration: boolean;
};

type UseRentalConfigurationTableColumnsReturnType = {
  columns: DataTableColumn<VenueRentalConfigurationType>[];
};

export const useRentalConfigurationTableColumns = ({
  handleDelete,
  handleEdit,
  userCanEditRentalConfiguration,
}: UseRentalConfigurationTableColumnsPropsType): UseRentalConfigurationTableColumnsReturnType => {
  const { t } = useTranslation('venueDetailsRental');

  const noDataPlaceholder = t('NO_DATA_PLACEHOLDER', { ns: 'common' });

  const columns: DataTableColumn<VenueRentalConfigurationType>[] = [
    {
      accessor: 'validFrom',
      title: t(`${TRANS_PATH}.VALID_FROM`),
      render: ({ validFrom }) =>
        validFrom ? formatDate(validFrom) : noDataPlaceholder,
      sortable: true,
    },
    {
      accessor: 'validUntil',
      title: t(`${TRANS_PATH}.VALID_UNTIL`),
      render: ({ validUntil }) =>
        validUntil ? formatDate(validUntil) : noDataPlaceholder,
      sortable: true,
    },
    {
      accessor: 'configuration',
      title: t(`${TRANS_PATH}.CONFIGURATION`),
      cellsStyle: () => ({ whiteSpace: 'nowrap' }),
      render: ({ configuration }) =>
        t(`CONFIGURATIONS.${configuration}`, { ns: 'venueDetails' }),
    },
    {
      accessor: 'version',
      title: t(`${TRANS_PATH}.VERSION`),
      cellsStyle: () => ({ whiteSpace: 'nowrap' }),
      render: ({ version }) =>
        version
          ? t(`CONFIGURATION_VERSIONS.${version}`, { ns: 'venueDetails' })
          : noDataPlaceholder,
    },
    {
      accessor: 'capacity',
      title: t(`${TRANS_PATH}.CAPACITY`),
      render: ({ capacity }) =>
        capacity ? formatNumber(capacity) : noDataPlaceholder,
    },
    {
      accessor: 'rentalFlatFee',
      title: t(`${TRANS_PATH}.RENTAL_FLAT_FEE`),
      render: ({ rentalFlatFee }) =>
        rentalFlatFee ? formatCurrency(rentalFlatFee) : noDataPlaceholder,
    },
    {
      accessor: 'rentalPercentageNet',
      title: t(`${TRANS_PATH}.RENTAL_PERCENTAGE_NET`),
      render: ({ rentalPercentageNet }) =>
        rentalPercentageNet
          ? formatPercent(rentalPercentageNet)
          : noDataPlaceholder,
    },
    {
      accessor: 'rentalPercentageGross',
      title: t(`${TRANS_PATH}.RENTAL_PERCENTAGE_GROSS`),
      render: ({ rentalPercentageGross }) =>
        rentalPercentageGross
          ? formatPercent(rentalPercentageGross)
          : noDataPlaceholder,
    },
    {
      accessor: 'minimumRental',
      title: t(`${TRANS_PATH}.MINIMUM_RENTAL`),
      render: ({ minimumRental }) =>
        minimumRental ? formatCurrency(minimumRental) : noDataPlaceholder,
    },
    {
      accessor: 'maximumRental',
      title: t(`${TRANS_PATH}.MAXIMUM_RENTAL`),
      render: ({ maximumRental }) =>
        maximumRental ? formatCurrency(maximumRental) : noDataPlaceholder,
    },
    {
      accessor: 'rentalSharePerTicket',
      title: t(`${TRANS_PATH}.RENTAL_SHARE_PER_TICKET`),
      render: ({ rentalSharePerTicket }) =>
        rentalSharePerTicket
          ? formatCurrency(rentalSharePerTicket)
          : noDataPlaceholder,
    },
    {
      accessor: 'rentalShareBasis',
      title: t(`${TRANS_PATH}.RENTAL_SHARE_BASIS`),
      render: ({ rentalShareBasis }) =>
        rentalShareBasis ? formatNumber(rentalShareBasis) : noDataPlaceholder,
    },
    {
      accessor: 'fixedVenueSideCosts',
      title: t(`${TRANS_PATH}.FIXED_VENUE_SIDE_COSTS`),
      render: ({ fixedVenueSideCosts }) =>
        fixedVenueSideCosts
          ? formatCurrency(fixedVenueSideCosts)
          : noDataPlaceholder,
    },
    {
      accessor: 'notes',
      title: t(`${TRANS_PATH}.NOTES`),
      render: ({ notes }) => notes ?? noDataPlaceholder,
    },
    {
      accessor: 'actions',
      title: t(`${TRANS_PATH}.ACTIONS`),
      titleStyle: { textAlign: 'center' },
      render: (record) => (
        <ActionsCell.Root>
          <ActionsCell.Edit onClick={() => handleEdit(record)} />
          <ActionsCell.Delete onClick={() => handleDelete(record.id)} />
        </ActionsCell.Root>
      ),
    },
  ];

  const filteredColumns = columns.filter((column) => {
    if (column.accessor === 'actions') {
      return userCanEditRentalConfiguration;
    }
    return true;
  });

  return { columns: filteredColumns };
};
